import { PlayIcon, ResetIcon, TrashIcon } from '@radix-ui/react-icons';
import { Badge, Flex, IconButton, Table, Text } from '@radix-ui/themes';
import { ITargetProps } from 'components/common/training-data-table/common';
import { DeltaCell } from 'components/common/training-data-table/delta-cell';
import { getFireVideoID } from 'components/common/video/annotated-player';
import { MachineContext } from 'contexts/machine.context';
import { parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import { t } from 'i18next';
import { ITrainingDataTableSampleRowProps } from 'interfaces/i-training-data-table';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import {
  LOCAL_DATETIME_FORMAT,
  LOCAL_TIMEZONE,
} from 'lib_ts/enums/datetime.enums';
import { BuildPriority } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useContext, useMemo } from 'react';

export const DataTableSampleRow = (props: ITrainingDataTableSampleRowProps) => {
  const { unitFactors } = useContext(MachineContext);

  const deltas = useMemo((): ITargetProps[] => {
    const output: ITargetProps[] = [
      {
        target:
          unitFactors.fromSpeedMph *
          TrajHelper.getSpeedFromTraj(props.targetTraj),
        value:
          unitFactors.fromSpeedMph *
          TrajHelper.getSpeedFromTraj(props.shot.traj),
        precision: 1,
      },
    ];

    switch (props.priority) {
      case BuildPriority.Breaks: {
        const shotBreaks = TrajHelper.getBreaksFromShot(props.shot);

        output.push(
          {
            target:
              (props.targetBreaks?.zInches ?? 0) * unitFactors.fromBreaksIn,
            value: shotBreaks.zInches * unitFactors.fromBreaksIn,
            precision: 1,
          },
          {
            target:
              -1 *
              (props.targetBreaks?.xInches ?? 0) *
              unitFactors.fromBreaksIn,
            value: -1 * shotBreaks.xInches * unitFactors.fromBreaksIn,
            precision: 1,
          }
        );
        break;
      }

      case BuildPriority.Default:
      case BuildPriority.Spins:
      default: {
        output.push(
          {
            target: props.targetBS.wx,
            value: props.shot.bs?.wx,
            precision: 0,
          },
          {
            target: props.targetBS.wy,
            value: props.shot.bs?.wy,
            precision: 0,
          },
          {
            target: props.targetBS.wz,
            value: props.shot.bs?.wz,
            precision: 0,
          }
        );
        break;
      }
    }

    return output;
  }, [
    unitFactors,
    props.priority,
    props.shot,
    props.targetBreaks,
    props.targetBS,
    props.targetTraj,
  ]);

  const videoID = getFireVideoID({
    annotated_video_id: props.shot.annotated_video_id,
    replay_video_id: props.shot.replay_video_id,
  });

  const isVideoDisabled = !videoID;

  const buttonCell = props.discardDisabled ? null : (
    <Table.Cell align="center">
      <Flex gap="1" justify="center">
        <IconButton
          variant="soft"
          size="1"
          color={props.isDiscarded ? RADIX.COLOR.NEUTRAL : RADIX.COLOR.DANGER}
          onClick={() => props.onArchiveShot(props.shot)}
        >
          {props.isDiscarded ? <ResetIcon /> : <TrashIcon />}
        </IconButton>
      </Flex>
    </Table.Cell>
  );

  if (props.isDiscarded) {
    return (
      <Table.Row data-created={props.shot._created}>
        <Table.Cell colSpan={deltas.length + 2}>
          <Text color={RADIX.COLOR.NEUTRAL}>
            {t('tr.sample')} {props.index + 1}
          </Text>{' '}
          <Badge variant="soft" color={RADIX.COLOR.DANGER}>
            Deleted
          </Badge>
        </Table.Cell>

        {buttonCell}
      </Table.Row>
    );
  }

  return (
    <Table.Row data-created={props.shot._created}>
      <Table.Cell
        title={format(parseISO(props.shot._created), LOCAL_DATETIME_FORMAT, {
          timeZone: LOCAL_TIMEZONE,
        })}
      >
        {t('tr.sample')} {props.index + 1}
      </Table.Cell>

      <Table.Cell align="center">
        <IconButton
          variant="soft"
          size="1"
          title={
            isVideoDisabled
              ? t('common.no-video-playback-msg').toString()
              : undefined
          }
          disabled={isVideoDisabled}
          onClick={() => props.onPlayVideo(props.shot)}
        >
          <PlayIcon />
        </IconButton>
      </Table.Cell>

      {deltas.map((v, i) => (
        <DeltaCell key={i} {...v} />
      ))}

      {buttonCell}
    </Table.Row>
  );
};
